"use client";

import { useId } from "react";
import { ActivityScenePreview } from "./reference-preview-primitives";

export type HIITDisplayPreviewProps = {
  participants: Array<{
    id: string;
    displayName: string;
    avatarUrl: string | null;
    username: string | null;
  }>;
};

/*
 * A functional-fitness box between rounds: a steel rig with rings on the left,
 * kettlebells on the rubber floor, a plyo box and battle ropes on the right,
 * and the interval clock — the scene's focal point — glowing red on the back
 * wall. Composed so the bottom-centre participant pill sits on open floor.
 *
 * Motion is two compositor-only CSS loops (the clock's colon and its wall
 * glow), hoisted once per page by React's <style precedence>, and off under
 * reduced motion.
 */
const HIIT_KEYFRAMES = `
@keyframes hiit-colon { 0%, 49% { opacity: 1 } 50%, 100% { opacity: 0.15 } }
@keyframes hiit-glow { 0%, 100% { opacity: 0.75 } 50% { opacity: 1 } }
.hiit-colon { animation: hiit-colon 1s steps(1, end) infinite }
.hiit-glow { animation: hiit-glow 3.2s ease-in-out infinite }
@media (prefers-reduced-motion: reduce) {
  .hiit-colon, .hiit-glow { animation: none }
}
`;

function useSvgIds() {
  const uid = useId().replace(/[^\w-]/g, "");
  return (name: string) => `hiit-${uid}-${name}`;
}

type IdFn = ReturnType<typeof useSvgIds>;

function SceneDefs({ id }: { id: IdFn }) {
  return (
    <svg className="absolute h-0 w-0" aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id={id("steel")} x1="0" x2="1" y1="0" y2="0">
          <stop offset="0" stopColor="#0d0d10" />
          <stop offset="0.45" stopColor="#3a3a42" />
          <stop offset="0.7" stopColor="#1c1c21" />
          <stop offset="1" stopColor="#0b0b0d" />
        </linearGradient>
        <radialGradient id={id("kb-red")} cx="0.36" cy="0.32" r="0.75">
          <stop offset="0" stopColor="#ff7a66" />
          <stop offset="0.4" stopColor="#d3261c" />
          <stop offset="1" stopColor="#5a0906" />
        </radialGradient>
        <radialGradient id={id("kb-black")} cx="0.36" cy="0.32" r="0.75">
          <stop offset="0" stopColor="#6b6b73" />
          <stop offset="0.45" stopColor="#25252a" />
          <stop offset="1" stopColor="#09090b" />
        </radialGradient>
        <linearGradient id={id("box-front")} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#c98e55" />
          <stop offset="1" stopColor="#8a5a2e" />
        </linearGradient>
        <linearGradient id={id("box-top")} x1="0" x2="1" y1="0" y2="0">
          <stop offset="0" stopColor="#e6b27a" />
          <stop offset="1" stopColor="#f3c896" />
        </linearGradient>
        <linearGradient id={id("cone")} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#ffd2b0" stopOpacity="0.2" />
          <stop offset="1" stopColor="#ffd2b0" stopOpacity="0" />
        </linearGradient>
      </defs>
    </svg>
  );
}

/** Back wall, ceiling light cones, rubber floor — stretches with the card. */
function Room({ id }: { id: IdFn }) {
  return (
    <svg
      className="absolute inset-0 h-full w-full"
      viewBox="0 0 300 220"
      preserveAspectRatio="none"
      aria-hidden="true"
      focusable="false"
    >
      {/* Block-wall courses */}
      {[34, 68, 102, 136].map((y) => (
        <rect
          key={y}
          x="0"
          y={y}
          width="300"
          height="1"
          fill="#000"
          opacity="0.28"
        />
      ))}
      {[60, 150, 240].map((x) => (
        <rect
          key={x}
          x={x}
          y="0"
          width="1"
          height="150"
          fill="#000"
          opacity="0.18"
        />
      ))}
      {/* Overhead light cones */}
      <polygon points="40,0 72,0 112,150 0,150" fill={`url(#${id("cone")})`} />
      <polygon
        points="228,0 260,0 300,150 188,150"
        fill={`url(#${id("cone")})`}
      />
      {/* Baseboard + red LED strip */}
      <rect x="0" y="144" width="300" height="7" fill="#0a0809" />
      <rect
        x="0"
        y="143"
        width="300"
        height="1.4"
        fill="#ff3b2f"
        opacity="0.85"
      />
      <rect
        x="0"
        y="140"
        width="300"
        height="6"
        fill="#ff3b2f"
        opacity="0.12"
      />
      {/* Rubber floor */}
      <rect x="0" y="151" width="300" height="69" fill="#141113" />
      {[158, 170, 188, 212].map((y) => (
        <rect
          key={y}
          x="0"
          y={y}
          width="300"
          height="0.8"
          fill="#000"
          opacity="0.55"
        />
      ))}
      {[
        [150, 150],
        [108, 40],
        [192, 260],
        [66, -70],
        [234, 370],
        [24, -180],
        [276, 480],
      ].map(([top, bottom]) => (
        <line
          key={top}
          x1={top}
          y1="151"
          x2={bottom}
          y2="220"
          stroke="#000"
          strokeOpacity="0.5"
          strokeWidth="0.8"
        />
      ))}
      {/* Red spill from the LED strip, and the clock's pool on the floor */}
      <rect
        x="0"
        y="151"
        width="300"
        height="10"
        fill="#ff3b2f"
        opacity="0.07"
      />
      <ellipse
        cx="150"
        cy="168"
        rx="120"
        ry="20"
        fill="#ff3b2f"
        opacity="0.06"
      />
    </svg>
  );
}

function Rig({ id }: { id: IdFn }) {
  return (
    <svg
      className="absolute top-0 left-[2%]"
      width="86"
      height="164"
      viewBox="0 0 86 164"
      aria-hidden="true"
      focusable="false"
    >
      {/* Uprights */}
      <rect x="6" y="0" width="7" height="160" fill={`url(#${id("steel")})`} />
      <rect x="70" y="0" width="7" height="160" fill={`url(#${id("steel")})`} />
      {/* J-hook holes */}
      {[54, 66, 78, 90, 102, 114].map((y) => (
        <g key={y}>
          <circle cx="9.5" cy={y} r="1" fill="#000" opacity="0.7" />
          <circle cx="73.5" cy={y} r="1" fill="#000" opacity="0.7" />
        </g>
      ))}
      {/* Pull-up bar + crossmember */}
      <rect x="0" y="22" width="86" height="4" rx="2" fill="#4a4a52" />
      <rect x="0" y="22" width="86" height="1.2" fill="#9a9aa6" opacity="0.6" />
      {/* Ring straps + rings */}
      <rect x="29" y="26" width="2" height="44" fill="#e8b23a" />
      <rect x="53" y="26" width="2" height="38" fill="#e8b23a" />
      <circle
        cx="30"
        cy="79"
        r="8.5"
        fill="none"
        stroke="#b9824d"
        strokeWidth="3.4"
      />
      <circle
        cx="54"
        cy="73"
        r="8.5"
        fill="none"
        stroke="#b9824d"
        strokeWidth="3.4"
      />
      <path
        d="M23.5 74 a8.5 8.5 0 0 1 5 -4"
        stroke="#f0c99a"
        strokeWidth="1.2"
        fill="none"
      />
      <path
        d="M47.5 68 a8.5 8.5 0 0 1 5 -4"
        stroke="#f0c99a"
        strokeWidth="1.2"
        fill="none"
      />
      {/* Rim light from the clock side */}
      <rect x="12" y="0" width="1" height="160" fill="#ff8a6a" opacity="0.45" />
      <rect x="76" y="0" width="1" height="160" fill="#ff8a6a" opacity="0.45" />
      {/* Base plates */}
      <rect x="2" y="157" width="15" height="5" rx="1" fill="#0b0b0d" />
      <rect x="66" y="157" width="15" height="5" rx="1" fill="#0b0b0d" />
    </svg>
  );
}

function Kettlebell({
  id,
  fill,
  size,
  className,
  handle,
}: {
  id: IdFn;
  fill: "kb-red" | "kb-black";
  size: number;
  className?: string;
  handle: string;
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size * 1.08}
      viewBox="0 0 32 34.5"
      aria-hidden="true"
      focusable="false"
    >
      <ellipse cx="16" cy="33" rx="12" ry="1.8" fill="#000" opacity="0.6" />
      <path
        d="M8.6 15 V9.5 a7.4 7.4 0 0 1 14.8 0 V15"
        fill="none"
        stroke={handle}
        strokeWidth="3.6"
        strokeLinecap="round"
      />
      <circle cx="16" cy="21.5" r="11" fill={`url(#${id(fill)})`} />
      <rect
        x="8"
        y="30.2"
        width="16"
        height="2"
        rx="1"
        fill="#000"
        opacity="0.5"
      />
      <path
        d="M24.5 15.5 a11 11 0 0 1 1.6 10"
        stroke="#ff9a7a"
        strokeOpacity="0.55"
        strokeWidth="1"
        fill="none"
      />
    </svg>
  );
}

function PlyoBox({ id }: { id: IdFn }) {
  return (
    <svg
      className="absolute right-[5%] bottom-[56px]"
      width="72"
      height="60"
      viewBox="0 0 72 60"
      aria-hidden="true"
      focusable="false"
    >
      <ellipse cx="34" cy="57" rx="36" ry="3" fill="#000" opacity="0.55" />
      {/* Top */}
      <polygon points="4,12 54,12 68,4 18,4" fill={`url(#${id("box-top")})`} />
      {/* Side */}
      <polygon points="54,12 68,4 68,48 54,56" fill="#6e4422" />
      {/* Front */}
      <rect
        x="4"
        y="12"
        width="50"
        height="44"
        fill={`url(#${id("box-front")})`}
      />
      {/* Hand-hold slot + stencil */}
      <rect x="20" y="17" width="18" height="5" rx="2.5" fill="#3d2410" />
      <text
        x="29"
        y="44"
        textAnchor="middle"
        fontFamily="ui-sans-serif, system-ui, sans-serif"
        fontWeight="900"
        fontSize="15"
        fill="#1a0f06"
        opacity="0.72"
      >
        24
      </text>
      {/* Wood grain */}
      <path
        d="M6 30 h46 M6 50 h46"
        stroke="#7a4b23"
        strokeWidth="0.6"
        opacity="0.6"
      />
      {/* Rim light on the leading edge */}
      <rect x="4" y="12" width="1.2" height="44" fill="#ffc9a3" opacity="0.7" />
      <rect x="4" y="12" width="50" height="1" fill="#ffe2c4" opacity="0.8" />
    </svg>
  );
}

function BattleRopes() {
  const ropeA = "M86 14 C 74 4, 64 26, 52 16 S 30 4, 20 20 S 8 30, 4 26";
  const ropeB = "M86 22 C 72 32, 64 12, 50 24 S 30 38, 20 30 S 10 22, 6 36";
  return (
    <svg
      className="absolute right-0 bottom-[10px]"
      width="88"
      height="46"
      viewBox="0 0 88 46"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d={ropeA}
        transform="translate(0 4)"
        stroke="#000"
        strokeOpacity="0.5"
        strokeWidth="6"
        fill="none"
      />
      <path
        d={ropeB}
        transform="translate(0 4)"
        stroke="#000"
        strokeOpacity="0.5"
        strokeWidth="6"
        fill="none"
      />
      {[ropeA, ropeB].map((d) => (
        <g key={d}>
          <path
            d={d}
            stroke="#3a3a42"
            strokeWidth="5.5"
            strokeLinecap="round"
            fill="none"
          />
          <path
            d={d}
            stroke="#121215"
            strokeWidth="5.5"
            strokeDasharray="1 2.6"
            strokeLinecap="butt"
            fill="none"
            opacity="0.45"
          />
          <path
            d={d}
            stroke="#ff9a7a"
            strokeOpacity="0.35"
            strokeWidth="1"
            fill="none"
            transform="translate(0 -1.6)"
          />
        </g>
      ))}
      {/* Taped handles */}
      <rect
        x="0"
        y="26"
        width="9"
        height="6"
        rx="2"
        fill="#d3261c"
        transform="rotate(-20 4 29)"
      />
      <rect
        x="2"
        y="36"
        width="9"
        height="6"
        rx="2"
        fill="#d3261c"
        transform="rotate(25 6 39)"
      />
      {/* Anchor */}
      <rect x="82" y="10" width="6" height="18" rx="2" fill="#0b0b0d" />
    </svg>
  );
}

/** The interval clock — HTML so the digits get a real text-shadow glow. */
function IntervalClock() {
  return (
    <div className="absolute top-[42px] left-1/2 -translate-x-1/2">
      <div className="hiit-glow absolute -inset-x-10 -inset-y-8 rounded-full bg-[radial-gradient(closest-side,rgba(255,59,47,0.38),rgba(255,59,47,0.12)_55%,transparent)]" />
      <div className="relative flex items-center gap-2 rounded-[7px] border border-white/10 bg-[linear-gradient(180deg,#1a1a1d,#060607)] px-2.5 py-1.5 shadow-[0_10px_24px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.08)]">
        <div className="flex flex-col items-start gap-[3px] leading-none">
          <span className="text-[7px] font-black tracking-[0.18em] text-[#46e08a] [text-shadow:0_0_6px_rgba(70,224,138,0.8)]">
            WORK
          </span>
          <span className="text-[7px] font-bold tracking-[0.18em] text-[#ffb08a]/70">
            R3/8
          </span>
        </div>
        <div className="font-mono text-[26px] leading-none font-black tracking-[0.02em] text-[#ff3b2f] tabular-nums [text-shadow:0_0_8px_rgba(255,59,47,0.95),0_0_18px_rgba(255,59,47,0.55)]">
          0<span className="hiit-colon">:</span>20
        </div>
      </div>
    </div>
  );
}

export function HIITDisplayPreview({ participants }: HIITDisplayPreviewProps) {
  const id = useSvgIds();

  return (
    <ActivityScenePreview
      label="HIIT"
      backgroundClassName="bg-[#1d1416]"
      participants={participants}
      scene={
        <>
          <style href="hiit-scene-keyframes" precedence="default">
            {HIIT_KEYFRAMES}
          </style>
          <SceneDefs id={id} />

          {/* Smoky crimson box, lit from the clock */}
          <div className="absolute inset-0 bg-[radial-gradient(120%_75%_at_50%_22%,#4a1a1d_0%,#2a1316_45%,#140c0e_100%)]" />
          <Room id={id} />

          <Rig id={id} />
          <IntervalClock />
          <PlyoBox id={id} />

          {/* Floor props, kept to the outer thirds */}
          <Kettlebell
            id={id}
            fill="kb-red"
            handle="#2b2b2f"
            size={34}
            className="absolute bottom-[22px] left-[4%]"
          />
          <Kettlebell
            id={id}
            fill="kb-black"
            handle="#3a3a40"
            size={26}
            className="absolute bottom-[14px] left-[calc(4%+34px)]"
          />
          <BattleRopes />
        </>
      }
      overlays={
        <div className="pointer-events-none absolute inset-0 z-10 bg-[radial-gradient(ellipse_110%_120%_at_50%_40%,transparent_45%,rgba(0,0,0,0.55)_100%)]" />
      }
    />
  );
}
